import type { Modifiers } from './doClick';
import type { TstaType } from 'clickim/src/injector/TstaType';
import type { StepQuirks } from 'clickim/src/common/models/step';
import type { IMouseEvent } from 'clickim/src/common/models/mouseStep';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

interface HTML5DNDEventData {
    transactionId: string;
    id: string;
    testResultId: string;
    eventType: string;
    events: IMouseEvent[];
    eventData: { modifiers?: Partial<Modifiers>; button?: number };
    quirks: StepQuirks;
    isDrag: true;
    useRecordedMousedown?: boolean;
    allEventsOnSameElement?: boolean;
    elementToFocusLocatedElement?: false | ILocatedElement;
    trackActiveElement?: boolean;
    locatedElement: ILocatedElement;
    isRoot?: boolean;
}
interface Context extends Pick<HTML5DNDEventData, 'allEventsOnSameElement' | 'events' | 'eventType' | 'eventData' | 'testResultId' | 'isDrag' | 'useRecordedMousedown' | 'trackActiveElement'> {
    eventIndex: number;
    stepId: string;
    element?: Element | null;
    lastDraggedElement?: HTMLElement;
}
declare function getLocatedElement(locatedElement: ILocatedElement | string): Element | null;


export const html5dndAction = function (eventData: HTML5DNDEventData, done: (data: { status: 'done' | 'failed'; result?: any; success: boolean; keep?: true }) => void) {
    const mouseEventConstructorSupported = typeof MouseEvent === 'function';
    const dragEventConstructorSupported = typeof DragEvent === 'function';
    const pointerEventConstructorSupported = typeof PointerEvent === 'function';
    const data = {} as { [key: string]: any };

    window.__unloadNavigator = resolve;

    window.addEventListener('unload', window.__unloadNavigator);

    function resolve(result?: string) {
        const status = {
            status: 'done' as const,
            result,
            success: true,
        };
        done(status);
    }

    function reject(result?: string | Error) {
        result ||= {} as any;
        const status = {
            status: 'failed' as const,
            result,
            success: false,
            keep: true as const,
        };

        done(status);
    }

    function convertType(type?: string) {
        if (!type?.toLowerCase) {
            return type;
        }
        type = type.toLowerCase();
        if (type === 'text') {
            return 'text/plain';
        }
        if (type === 'url') {
            return 'text/uri-list';
        }
        return type;
    }

    function createDataTransfer() {
        try {
            return new DataTransfer();
        } catch (err) {
            return {
                data: {},
                setData(type: string, val: any) {
                    data[convertType(type)!] = val;
                },
                getData(type: string) {
                    return data[convertType(type)!];
                },
            } as unknown as DataTransfer;
        }
    }

    const dragFromElementEvents = ['drag', 'dragstart', 'dragend'];
    const pointerEvents = ['pointerup', 'pointerdown', 'pointermove'];
    const dragEvents = dragFromElementEvents.concat(['drop', 'dragenter', 'dragover']);

    function findClosestDraggable(element?: Element | null) {
        // drag events are always fired on the closest element with draggable=true
        // rather than on the target mouse element - so when dispatching a drag
        // event we need to fire the correct target
        let current = element as HTMLElement | null;
        while (current && current !== document.documentElement) {
            if (current.draggable) {
                return current;
            }
            current = current.parentElement;
        }
        return null;
    }

    function getElement(event: Event, context: Context, currentEvent: IMouseEvent) {
        const element = context.element;
        const { dispatchDragEventsOnClosestDraggable } = currentEvent;
        if (dragFromElementEvents.includes(event.type) && dispatchDragEventsOnClosestDraggable) {
            if (!element && context.lastDraggedElement) {
                // fallback for elements being removed from DOM, but parent draggable still being there
                return context.lastDraggedElement;
            }

            const draggable = findClosestDraggable(element);
            if (draggable) {
                context.lastDraggedElement = draggable;
                return draggable;
            }
        }
        return element;
    }

    function getEventPosition(userEvent: IMouseEvent, isDrag: true, element: Element) {
        function isWithinBounds(start: number, end: number, point: number) {
            return (point > start) && (point < end);
        }

        const pointerPosition = userEvent.pointerPosition || {} as Partial<NonNullable<IMouseEvent['pointerPosition']>>;
        // Technically should always be true here.
        if (isDrag) {
            return { x: pointerPosition.originX || 0, y: pointerPosition.originY || 0 };
        }
        const rect = element.getBoundingClientRect();
        const clientX = pointerPosition.originX && isWithinBounds(rect.left, rect.left + rect.width, pointerPosition.originX) ? pointerPosition.originX : rect.left + (rect.width / 2);
        const clientY = pointerPosition.originY && isWithinBounds(rect.top, rect.top + rect.height, pointerPosition.originY) ? pointerPosition.originY : rect.top + (rect.height / 2);
        return { x: clientX, y: clientY };
    }

    function getMouseEvent(userEvent: IMouseEvent, _eventData: HTML5DNDEventData['eventData'], context: Context) {
        const modifiers = _eventData?.modifiers || {};
        const pos = getEventPosition(userEvent, context.isDrag, context.element!);
        const button = _eventData?.button || 0;
        const eventType = userEvent.event;
        if (pointerEvents.includes(eventType)) {
            return createPointerEvent(eventType, modifiers, pos.x, pos.y, button);
        }
        if (dragEvents.includes(eventType)) {
            return createDragEvent(eventType, modifiers, pos.x, pos.y);
        }
        return createMouseEvent(eventType, modifiers, pos.x, pos.y, button);
    }

    function getEventDictionary(modifiers: Partial<Modifiers>, clientX: number, clientY: number): PointerEventInit {
        return {
            screenX: 0,
            screenY: 0,
            clientX,
            clientY,
            ctrlKey: Boolean(modifiers.ctrl),
            altKey: Boolean(modifiers.alt),
            shiftKey: Boolean(modifiers.shift),
            metaKey: Boolean(modifiers.meta),
            bubbles: true,
            cancelable: true,
            composed: true,
        };
    }

    function createPointerEvent(eventType: string, modifiers: Partial<Modifiers>, clientX: number, clientY: number, button: number) {
        if (pointerEventConstructorSupported) {
            const eventProperties = getEventDictionary(modifiers, clientX, clientY);
            eventProperties.pointerType = 'mouse';
            eventProperties.isPrimary = true;
            return new window.PointerEvent(eventType, eventProperties);
        }
        const event = document.createEvent('PointerEvent');
        // @ts-expect-error this function was removed from all modern browsers
        event.initPointerEvent(
            eventType,
            true, /* bubbles */
            true, /* cancelable */
            document.defaultView, /* view */
            1, /* detail */
            0, /* screenX */
            0, /* screenY */
            clientX, /* clientX */
            clientY, /* clientY */
            Boolean(modifiers.ctrl), /* ctrl */
            Boolean(modifiers.alt), /* alt */
            Boolean(modifiers.shift), /* shift */
            Boolean(modifiers.meta), /* meta */
            button, /* button */
            document.body ? document.body.parentNode : document.documentElement,
            0, /* offsetYArg */
            0, /* offsetXArg */
            0, /* widthArg */
            0, /* heightArg */
            0, /* pressure */
            0, /* rotation */
            0, /* tiltX */
            0, /* tiltY */
            0, /* pointerIdArg */
            'mouse', /* pointerType */
            0, /* hwTimestampArg */
            true, /* isPrimary */
        );
        return event;
    }

    function createMouseEvent(eventType: string, modifiers: Partial<Modifiers>, clientX: number, clientY: number, button: number) {
        const event = mouseEventConstructorSupported ? new MouseEvent('click', { composed: true }) : document.createEvent('MouseEvents');
        event.initMouseEvent(
            eventType,
            true, /* bubbles */
            true, /* cancelable */
            document.defaultView!, /* view */
            1, /* detail */
            0, /* screenX */
            0, /* screenY */
            clientX, /* clientX */
            clientY, /* clientY */
            Boolean(modifiers.ctrl), /* ctrl */
            Boolean(modifiers.alt), /* alt */
            Boolean(modifiers.shift), /* shift */
            Boolean(modifiers.meta), /* meta */
            button, /* button */
            document.body ? document.body.parentNode : document.documentElement);
        return event;
    }

    function createDragEvent(eventType: string, modifiers: Partial<Modifiers>, clientX: number, clientY: number) {
        if (eventType === 'dragstart') {
            window.TSTA ||= {} as TstaType;
            window.TSTA.dataTransfer = createDataTransfer();
        }

        if (!dragEventConstructorSupported) {
            const event = document.createEvent('CustomEvent');
            event.initCustomEvent(eventType, true, true, null);
            Object.assign(event, { dataTransfer: window.TSTA.dataTransfer });
            return event;
        }

        const eventProperties = getEventDictionary(modifiers, clientX, clientY);
        const event = new window.DragEvent(eventType, eventProperties);
        Object.defineProperties(event, {
            dataTransfer: { get() { return window.TSTA.dataTransfer; } },
        });
        return event;
    }

    function executeAsyncEvent(event: Event, context: Context, currentEvent: IMouseEvent) {
        const element = getElement(event, context, currentEvent);
        element?.dispatchEvent(event);
    }

    function shouldSkipEvent(currentEvent: IMouseEvent, context: Context) {
        function isClickInDragAndDrop() {
            return currentEvent.event === 'click' &&
                context.isDrag &&
                !context.allEventsOnSameElement;
        }
        return isClickInDragAndDrop();
    }

    function nextAsyncEvent(context: Context, currentEvent: IMouseEvent, nextEvent: IMouseEvent) {
        if (nextEvent) {
            const delay = Math.min(nextEvent.timeStamp! - currentEvent.timeStamp!, 40);
            setTimeout(() => {
                executeAsyncNext(context);
            }, delay);
        } else {
            if (window.__unloadNavigator) {
                window.removeEventListener('unload', window.__unloadNavigator);
            }
            resolve();
        }
    }

    function executeAsyncNext(context: Context) {
        let event;
        const currentEvent = context.events[context.eventIndex];
        const nextEvent = context.events[++context.eventIndex];
        try {
            context.element = getLocatedElement((currentEvent as any).locatedElement);
            // @ts-expect-error we are passing the wrong eventData. not sure if changing this would be safe, or if it is needed.
            event = getMouseEvent(currentEvent, eventData, context);
        } catch (err) {
            return reject(`exception in get event in drag step:${err.message}`);
        }

        if (shouldSkipEvent(currentEvent, context)) {
            return nextAsyncEvent(context, currentEvent, nextEvent);
        }

        if (event) {
            try {
                executeAsyncEvent(event, context, currentEvent);
            } catch (err) {
                return reject(`exception in executeEvent in drag step:${err.message}`);
            }
        } else {
            return reject(`cannot execute event ${currentEvent.event}`);
        }
        nextAsyncEvent(context, currentEvent, nextEvent);
        return undefined;
    }

    const context: Context = {
        eventIndex: 0,
        allEventsOnSameElement: eventData.allEventsOnSameElement,
        events: eventData.events,
        eventType: eventData.eventType,
        eventData: eventData.eventData,
        stepId: eventData.id,
        testResultId: eventData.testResultId,
        isDrag: eventData.isDrag,
        useRecordedMousedown: eventData.useRecordedMousedown,
        trackActiveElement: eventData.trackActiveElement,
    };

    setTimeout(() => {
        try {
            executeAsyncNext(context);
        } catch (err) {
            reject(err);
        }
    }, 0);
};
